// src/hooks/useNetworkStatus.ts - Hook pour détecter le statut réseau

import { useState, useEffect } from 'react';
import { recordOnline, recordOffline } from '../utils/networkMonitor';

export interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType?: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  downlink?: number; // Mbps
  rtt?: number; // ms (Round Trip Time)
}

/**
 * Hook pour détecter le statut réseau et la qualité de la connexion
 * 
 * @returns {NetworkStatus} État actuel de la connexion réseau
 * 
 * @example
 * const { isOnline, isSlowConnection, connectionType } = useNetworkStatus();
 * 
 * if (!isOnline) {
 *   return <OfflineBanner />;
 * }
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    // État initial basé sur navigator.onLine et navigator.connection
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    const isOnline = navigator.onLine;
    const effectiveType = connection?.effectiveType || 'unknown';
    const downlink = connection?.downlink;
    const rtt = connection?.rtt;
    
    const isSlowConnection = !isOnline || effectiveType === 'slow-2g' || effectiveType === '2g';
    
    return {
      isOnline,
      isSlowConnection,
      connectionType: effectiveType as NetworkStatus['connectionType'],
      downlink,
      rtt,
    };
  });

  useEffect(() => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine;
      const effectiveType = connection?.effectiveType || 'unknown';
      const downlink = connection?.downlink;
      const rtt = connection?.rtt;

      const isSlowConnection = !isOnline || effectiveType === 'slow-2g' || effectiveType === '2g';

      // P9 : monitoring léger – enregistrer offline/online
      if (isOnline) recordOnline();
      else recordOffline();

      setNetworkStatus({
        isOnline,
        isSlowConnection,
        connectionType: effectiveType as NetworkStatus['connectionType'],
        downlink,
        rtt,
      });
    };

    // Écouter les changements d'état réseau
    const handleOnline = () => updateNetworkStatus();
    const handleOffline = () => updateNetworkStatus();

    // Écouter les changements de qualité de connexion (si supporté)
    const handleConnectionChange = () => updateNetworkStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, []);

  return networkStatus;
};

